import React, { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";

const animationVariants = {
  fadeUp: {
    hidden: { opacity: 0, y: 80 },
    visible: { opacity: 1, y: 0 },
  },
  slideRight: {
    hidden: { opacity: 0, x: -100 },
    visible: { opacity: 1, x: 0 },
  },
  slideLeft: {
    hidden: { opacity: 0, x: 100 },
    visible: { opacity: 1, x: 0 },
  },
  scaleUp: {
    hidden: { opacity: 0, scale: 0.85 },
    visible: { opacity: 1, scale: 1 },
  },
  blurIn: {
    hidden: { opacity: 0, filter: "blur(20px)", y: 40 },
    visible: { opacity: 1, filter: "blur(0px)", y: 0 },
  },
  clipReveal: {
    hidden: { opacity: 0, clipPath: "inset(10% 10% 10% 10%)" },
    visible: { opacity: 1, clipPath: "inset(0% 0% 0% 0%)" },
  },
  rotateIn: {
    hidden: { opacity: 0, rotateX: 15, y: 60, perspective: 1000 },
    visible: { opacity: 1, rotateX: 0, y: 0, perspective: 1000 },
  },
  splitReveal: {
    hidden: { opacity: 0, y: 60, skewY: 3 },
    visible: { opacity: 1, y: 0, skewY: 0 },
  },
};

const SectionReveal = ({
  children,
  animation = "fadeUp",
  delay = 0,
  duration = 0.9,
  className = "",
  withParallax = false,
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: "-15% 0px -15% 0px",
  });

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const parallaxY = useTransform(scrollYProgress, [0, 1], [30, -30]);

  const variant = animationVariants[animation] || animationVariants.fadeUp;

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variant}
      transition={{
        duration: duration,
        delay: delay,
        ease: [0.22, 1, 0.36, 1],
      }}
      style={withParallax ? { y: parallaxY } : undefined}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StaggerChildren = ({ children, staggerDelay = 0.08, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: "-10% 0px -10% 0px",
  });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: staggerDelay,
          },
        },
      }}
      className={className}
    >
      {React.Children.map(children, (child) => (
        <motion.div
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: { opacity: 1, y: 0 },
          }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
};

const TextReveal = ({ text, className = "", tag: Tag = "span" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const words = text.split(" ");

  return (
    <Tag ref={ref} className={className}>
      {words.map((word, i) => (
        <motion.span
          key={i}
          className="inline-block mr-[0.3em]"
          initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
          animate={
            isInView
              ? { opacity: 1, y: 0, filter: "blur(0px)" }
              : {}
          }
          transition={{
            duration: 0.5,
            delay: i * 0.04,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          {word}
        </motion.span>
      ))}
    </Tag>
  );
};

export { SectionReveal, StaggerChildren, TextReveal };
